/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { FormEvent, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { api } from "../../../api/GeneratedApi";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { UserAtom } from "../../../atoms/UserAtom";
import { SettingsTab } from "../../../pages/settings";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { AllIntegrationsAtom } from "./Integrations";
import { useEffect } from "react";
import { ShopifyAPI } from "../../../api/ShopifyAPI";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const TitleWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  > img {
    width: 67px;
    height: 67px;
  }
`;

//!

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
    border-bottom: unset;
    padding-bottom: unset;
    margin-bottom: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;
//!

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > svg {
    color: #fb972e;
    margin-right: 8px;
  }
`;

interface IShopifyProps {
  handleSelected?: (vle: SettingsTab) => void;
}

export default function Shopify({
  handleSelected,
}: IShopifyProps): JSX.Element | null {
  const user = useRecoilValue(UserAtom);
  const [apiKey, setApiKey] = useState<string>("");
  const [url, setURL] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const setAllIntegrations = useSetRecoilState(AllIntegrationsAtom);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!apiKey) {
      toast("Fill the API Key field", {
        type: "error",
      });

      return;
    }

    if (!user) return;

    setIsLoading(true);

    if (apiKey) {
      try {
        const result = await ShopifyAPI.checkDomainAndApiKey({ apiKey, url });

        if (result.ok) {
          let parsedUrl = url.trim();

          // Guarantee that the URL starts with https:// and DO NOT ends with /
          if (parsedUrl.includes("//")) {
            parsedUrl = parsedUrl.split("//")[1];
            parsedUrl = `https://${parsedUrl}`;
          }

          if (parsedUrl.endsWith("/"))
            parsedUrl = parsedUrl.substring(0, parsedUrl.length - 1);

          await IntegrationAPI.create({
            integration: {
              userUid: user.userUid,
              args: {
                apiKey: apiKey.trim(),
                url: parsedUrl,
              },
              type: "SHOPIFY",
            },
          });

          await ShopifyAPI.createWebhooks();

          let integrations = await IntegrationAPI.getAll();

          setAllIntegrations(integrations);

          // Concurrency solved like a monkey -Palhari
          // no time bro
          setTimeout(() => {
            handleSelected(SettingsTab.SHOPIFYPRODUCTS);
          }, 100);
        }
      } catch (err) {
        console.log(err);

        toast(
          "Error on verifying Shopify Admin API access token and store URL. Check it and try again.",
          {
            type: "error",
          }
        );
      } finally {
        setIsLoading(false);
      }
    }

    return null;
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleSubmit(event);
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [apiKey]);

  return (
    <Container>
      <TitleWrapper>
        <TextWrapper>
          <MainTitle>Shopify Integration</MainTitle>
          <MainText>
            To integrate your Shopify account with BerserkerMail, please provide
            your Shopify Admin API access token and store URL in the section
            below.
          </MainText>
        </TextWrapper>

        <img src="./images/shopify.png" alt="shopify" />
      </TitleWrapper>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Admin API access token</SecondaryTitle>
            <MainText>Your Admin API access token.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 337px;
            `}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Put your Admin API access token here"
          />
        </SpaceBetween>

        <SpaceBetween
          css={css`
            margin-top: 16px;
          `}
        >
          <div className="flex-column-wrapper">
            <SecondaryTitle>Store URL</SecondaryTitle>
            <MainText>Your store URL.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 337px;
            `}
            value={url}
            onChange={(e) => setURL(e.target.value)}
            placeholder="Put your store url here (eg: https://my-store.myshopify.com)"
          />
        </SpaceBetween>
      </FormContent>

      <TextWrapper>
        <MainTitle>Generating the Admin API access token</MainTitle>
        <MainText>
          Goto your <b>Shopify</b> dashboard, <b>Settings</b>, navigate to{" "}
          <b>Apps and sales channels</b>, then click on <b>Develop Apps</b>.
          <br />
          <br />
          If it's your <b>first time</b> setting up an app you will need to{" "}
          <b>Allow custom app development</b>. Click on the{" "}
          <b>Allow custom development button</b>, read the terms and confirm,
          clicking again on <b>Allow custom app development</b> one last time.
          <br />
          <br />
          You can now click on <b>Create an app</b> and create the app with a
          name of your chosing.
          <br />
          <br />
          Click on <b>Configure Admin API scopes</b> and select the following
          scopes:
          <br />
          <ul>
            <li>
              <code>read_products</code>
            </li>
            <li>
              <code>read_orders</code>
            </li>
            <li>
              <code>read_customers</code>
            </li>
          </ul>
          Click <b>Save</b> and then <b>Install app</b>.
          <br />
          Your <b>Admin API access token</b> can be viewed by clicking on{" "}
          <b>Reveal token once</b>. Make sure to save it!
        </MainText>
      </TextWrapper>

      <FooterColumn>
        <StyledButton
          isLoading={isLoading}
          onClick={handleSubmit}
          disable={!apiKey}
        >
          Connect with Shopify
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
